import { useEffect, useState } from "react";

import { SpecialZoomLevel, Worker } from "@react-pdf-viewer/core";

import { Viewer } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";

import { pageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import "@react-pdf-viewer/page-navigation/lib/styles/index.css";

import { toolbarPlugin } from "@react-pdf-viewer/toolbar";
import "@react-pdf-viewer/toolbar/lib/styles/index.css";

import {
  Button,
  Backdrop,
  CircularProgress,
  Stack,
  Box,
  Typography,
} from "@mui/material";
import { Queue } from "@mui/icons-material";

import _ from "lodash";
import { enqueueSnackbar, SnackbarProvider } from "notistack";

import {
  BACKEND_URL,
  CONSUMER_URL
} from "../../helpers/variables.js";
import confirm from "../../Components/Confirm.tsx";
import GroupSelector from "./GroupSelector.jsx";
import { useBill } from "../../Components/Contexts/Bill.jsx";
import { useSearchParams } from "react-router-dom";

const FilePanel = () => {
  let { bill, setBill, loading, setLoading } = useBill();

  const [searchParams] = useSearchParams();

  const billID = searchParams.get("bill_id");

  const toolbarPluginInstance = toolbarPlugin();
  const { Toolbar, renderDefaultToolbar } = toolbarPluginInstance;

  const pageNavigationPluginInstance = pageNavigationPlugin();
  const { jumpToPage } = pageNavigationPluginInstance;

  const [checkedPages, setCheckedPages] = useState([]);
  const [pageMeta, setPageMeta] = useState({});

  const { allPages = [] } = pageMeta;

  useEffect(() => {
    getBill();
  }, []);

  async function getBill() {
    setLoading(true);
    let res = await fetch(`${BACKEND_URL}/get_bill?bill_id=${billID}`);
    let data = await res.json();
    setBill(data);
    setLoading(false);
  }

  /**
   *
   * @param {import("@react-pdf-viewer/core").DocumentLoadEvent} param0
   */
  async function onDocumentLoad({ doc: { numPages } }) {
    let allPages = Array.from({ length: numPages }, (_, k) => k + 1);

    let res = await fetch(
      `${BACKEND_URL}/page_metadata?file_id=${bill["file_table_id"]}`,
    );
    let data = await res.json();

    setPageMeta({
      lockedPages: data.locked_pages,
      pageGroups: data.page_groups,
      allPages,
    });

    if (allPages.length === 1) {
      const page = allPages[0];

      const isInLockedPages = data.locked_pages.includes(page);
      const isInPageGroups = data.page_groups.flat().includes(page);

      if (!isInLockedPages && !isInPageGroups) {
        setCheckedPages(allPages);
      }
    }
  }

  async function addToQueue() {
    if (checkedPages.length === 0) {
      enqueueSnackbar("Select at least one page");
      return;
    }
    let userConfirmation = await confirm({
      message: `Are you sure you want to add the following pages to the queue: ${_.sortBy(
        checkedPages,
      ).join(", ")}?`,
    });
    if (!userConfirmation) return;

    setLoading(true);
    await fetch(
      `${CONSUMER_URL}/add_to_queue?file_table_id=${bill["file_table_id"]}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(checkedPages),
      },
    );
    setLoading(false);
    setPageMeta((prev) => ({
      ...prev,
      pageGroups: [...prev.pageGroups, checkedPages],
    }));
    setCheckedPages([]);
  }

  return (
    <SnackbarProvider
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
    >
      {bill && (
        <Stack direction={"row"} height={"100%"}>
          <Toolbar>
            {({ CurrentPageLabel }) => (
              <CurrentPageLabel>
                {({ currentPage }) => (
                  <Stack p={1} gap={1} minWidth={250} height={"100%"}>
                    <Typography variant="h6">Grouping</Typography>
                    <GroupSelector
                      pageMeta={pageMeta}
                      checkedPages={checkedPages}
                      setCheckedPages={setCheckedPages}
                      jumpToPage={jumpToPage}
                      currentPage={currentPage}
                    />
                    <Button
                      variant="contained"
                      fullWidth
                      endIcon={<Queue />}
                      onClick={addToQueue}
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      Add to Queue
                    </Button>
                  </Stack>
                )}
              </CurrentPageLabel>
            )}
          </Toolbar>
          <Stack flexGrow={1} borderLeft={1} borderColor={"lightgray"}>
            <Toolbar>
              {renderDefaultToolbar((slots) => ({
                ...slots,
                SwitchTheme: () => null,
                Open: () => null,
              }))}
            </Toolbar>
            <Box sx={{ flexGrow: 1, overflow: "auto" }}>
              <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
                <Viewer
                  fileUrl={bill["preassigned_url"]}
                  onDocumentLoad={(e) => onDocumentLoad(e)}
                  plugins={[
                    toolbarPluginInstance,
                    pageNavigationPluginInstance,
                  ]}
                  defaultScale={SpecialZoomLevel.PageWidth}
                />
              </Worker>
            </Box>
          </Stack>
        </Stack>
      )}

      <Backdrop open={loading}>
        <CircularProgress />
      </Backdrop>
    </SnackbarProvider>
  );
};

export default FilePanel;
